import { HttpStatusCode, isAxiosError } from 'axios';
import { isIapProtectedUrl, probeIapSession, redirectedByIap, refusalSource } from 'security/iap';
import { iapRefusalDetails } from 'security/iap-details';
import { logout, setIapAuthRequired, setMessages } from 'store/actions/actions';
import { convertErrors } from 'utils/convertError';
const dispatchErrors = (dispatch, errors) => {
    dispatch(setMessages(errors.map((m) => ({ value: m.message, severity: 'error' }))));
};
class APIAggregateError extends Error {
    subErrors;
    constructor(subErrors) {
        super(`Multiple errors ${subErrors.map((s) => s.message).join(', ')}`);
        this.subErrors = subErrors;
        this.name = 'APIAggregateError';
    }
}
export const createAsyncMiddleware = (state, dispatch) => async (action) => {
    try {
        return await action({
            dispatch,
            state
        });
    }
    catch (error) {
        let refusedByIap = false;
        if (error && isAxiosError(error)) {
            const refused = error.response?.status === HttpStatusCode.Unauthorized ||
                error.response?.status === HttpStatusCode.Forbidden;
            if (isIapProtectedUrl(error.config?.url)) {
                /*
                 * A 401 here is not proof of an IAP refusal: api-service answers 401
                 * for a missing permission, and the url-map prefix is case-sensitive
                 * while Express routing is not, so the app answers some /admin paths
                 * itself. Only an IAP-generated refusal is fixable by signing in; an
                 * unattributable one is settled by asking the origin. Any other
                 * readable status means the request got past IAP, unless IAP
                 * redirected it first.
                 */
                const source = redirectedByIap(error.config?.url, error.request?.responseURL)
                    ? 'iap'
                    : refused
                        ? refusalSource(error.response?.headers)
                        : error.response
                            ? 'app'
                            : 'unknown';
                if (source === 'iap' ||
                    (source === 'unknown' &&
                        navigator.onLine &&
                        (await probeIapSession()) === 'refused')) {
                    dispatch(setIapAuthRequired({
                        details: iapRefusalDetails(error.response?.status, error.response?.data),
                        unreadable: !error.response
                    }));
                    refusedByIap = true;
                }
            }
            else if (refused) {
                dispatch(logout()); // TODO: Bug risk?
                window.history.pushState('', '', '/signin'); // can't use react-router here, still adds history w/o reload
                return undefined;
            }
        }
        if (isAxiosError(error) && error.response) {
            const errors = refusedByIap
                ? []
                : convertErrors(error.response.data);
            if (errors.length >= 1) {
                dispatchErrors(dispatch, errors);
            }
            throw new APIAggregateError(errors);
        }
        throw error;
    }
};
